export type AppRoute =
  | { kind: 'home'; tableId?: string }
  | { kind: 'play'; tableId: string }
  | { kind: 'edit'; tableId: string }
  | { kind: 'create'; sourceTableId?: string };

function cleanTableId(value: string | null | undefined): string | null {
  const cleaned = value?.trim();
  return cleaned ? cleaned : null;
}

function decodedRouteId(pathname: string, prefix: string): string | null {
  if (!pathname.startsWith(prefix)) return null;
  const encoded = pathname.slice(prefix.length).split('/')[0] ?? '';
  if (!encoded) return null;
  try {
    return cleanTableId(decodeURIComponent(encoded));
  } catch {
    return null;
  }
}

export function parseAppRoute(pathname: string, search = ''): AppRoute {
  const params = new URLSearchParams(search);
  const legacyTableId = cleanTableId(params.get('table'));

  const playId = decodedRouteId(pathname, '/play/');
  if (playId) return { kind: 'play', tableId: playId };

  const editId = decodedRouteId(pathname, '/edit/');
  if (editId) return { kind: 'edit', tableId: editId };

  if (pathname === '/create' || pathname === '/create/') {
    return legacyTableId ? { kind: 'create', sourceTableId: legacyTableId } : { kind: 'create' };
  }

  if (params.get('play') === '1' && legacyTableId) return { kind: 'play', tableId: legacyTableId };
  if (params.get('edit') === '1' && legacyTableId) return { kind: 'edit', tableId: legacyTableId };
  return legacyTableId ? { kind: 'home', tableId: legacyTableId } : { kind: 'home' };
}

function routeUrl(baseHref: string): URL {
  const url = new URL(baseHref);
  url.hash = '';
  return url;
}

export function buildHomePath(tableId?: string | null): string {
  if (!tableId) return '/';
  return `/?table=${encodeURIComponent(tableId)}`;
}

export function buildPlayPath(tableId: string): string {
  const encoded = encodeURIComponent(tableId);
  // Keep the table query during the migration window because older clients know
  // how to restore a selected table from it. The pathname is the canonical human-
  // readable route; the redundant query can be removed once all clients are path-aware.
  return `/play/${encoded}?table=${encoded}`;
}

export function buildEditPath(tableId: string): string {
  const encoded = encodeURIComponent(tableId);
  return `/edit/${encoded}?table=${encoded}&edit=1`;
}

export function buildCreatePath(sourceTableId?: string | null): string {
  const params = new URLSearchParams({ edit: '1' });
  if (sourceTableId) params.set('table', sourceTableId);
  return `/create?${params.toString()}`;
}

export function canonicalRoutePath(route: AppRoute): string {
  switch (route.kind) {
    case 'play': return buildPlayPath(route.tableId);
    case 'edit': return buildEditPath(route.tableId);
    case 'create': return buildCreatePath(route.sourceTableId);
    case 'home': return buildHomePath(route.tableId);
  }
}

export function absoluteRouteUrl(path: string, baseHref = 'https://ballsofpin.com/'): string {
  const url = routeUrl(baseHref);
  return new URL(path, url.origin).toString();
}

export function routeTableId(pathname: string, search = ''): string | null {
  const route = parseAppRoute(pathname, search);
  if (route.kind === 'create') return route.sourceTableId ?? null;
  return route.tableId ?? null;
}
